import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, IndianRupee, Package, Users, ArrowRight, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  'Picked Up': '#1E8E5A', Processing: '#C0392B', 'Ready for Pickup': '#E08E2B', Cancelled: '#9A8F8B',
};

const STATUS_BADGE = {
  'Picked Up': 'badge-delivered',
  Processing: 'badge-processing',
  'Ready for Pickup': 'badge-shipped',
  Cancelled: 'badge-cancelled',
};
const getStatusBadge = (status) => STATUS_BADGE[status] || 'badge-processing';

const Dashboard = () => {
  const { authHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { headers: authHeader })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const statCards = [
    { label: 'Total Orders', value: stats?.totalOrders || 0, icon: ShoppingBag, growth: stats?.orderGrowth, prefix: '' },
    { label: 'Total Revenue', value: `₹${(stats?.totalRevenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee, growth: stats?.revenueGrowth, prefix: '' },
    { label: 'Total Products', value: stats?.totalProducts || 0, icon: Package, growth: null, prefix: '' },
  ];

  const donutData = Object.entries(stats?.statusBreakdown || {}).map(([name, value]) => ({ name, value }));
  const totalOrders = donutData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="p-6 space-y-6 bg-[#FDF8F3] min-h-full" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Dashboard</h1>
          <p className="text-[#7A6A63] text-sm mt-0.5">Welcome back, Admin! Here's what's happening today.</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map(card => (
          <div key={card.label} className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid={`stat-${card.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{card.label}</p>
                <p className="font-playfair text-2xl font-bold text-[#1F1410] mt-1">{card.value}</p>
                {card.growth !== null && card.growth !== undefined && (
                  <p className={`text-xs mt-1 flex items-center gap-0.5 ${parseFloat(card.growth) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    <TrendingUp size={11} />
                    {parseFloat(card.growth) >= 0 ? '+' : ''}{card.growth}% from last week
                  </p>
                )}
              </div>
              <div className="w-10 h-10 rounded-full bg-[#7A1125]/10 flex items-center justify-center">
                <card.icon size={18} className="text-[#7A1125]" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="sales-chart">
          <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Sales Overview (This Week)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats?.salesChart || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7A1125" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#7A1125" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECE0DA" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#7A6A63' }} />
              <YAxis tick={{ fontSize: 11, fill: '#7A6A63' }} tickFormatter={v => `₹${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
              <Tooltip formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="#7A1125" fill="url(#colorRevenue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Donut */}
        <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="order-status-chart">
          <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Order Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" label={false}>
                {donutData.map(entry => (
                  <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#7A1125'} />
                ))}
              </Pie>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="text-center -mt-4">
            <p className="text-2xl font-bold text-[#1F1410]">{totalOrders}</p>
            <p className="text-xs text-[#7A6A63]">Total</p>
          </div>
        </div>
      </div>

      {/* Recent orders + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent orders */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#ECE0DA] shadow-sm overflow-hidden" data-testid="recent-orders-table">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#ECE0DA]">
            <h3 className="font-playfair font-semibold text-[#1F1410]">Recent Orders</h3>
            <Link to="/admin/orders" className="text-xs text-[#7A1125] font-semibold hover:underline">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#FDF8F3]">
                  {['Order #', 'Customer', 'Status', 'Total'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#ECE0DA]">
                {(stats?.recentOrders || []).slice(0, 8).map(order => (
                  <tr key={order._id} className="hover:bg-[#FDF8F3] transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-[#7A6A63]">{order.orderNumber}</td>
                    <td className="px-4 py-3 font-medium text-[#1F1410]">{order.customerName}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadge(order.status)}`}>{order.status}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#7A1125]">₹{order.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="quick-actions">
          <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { label: 'Add New Product', href: '/admin/products/new' },
              { label: 'Add New Category', href: '/admin/categories' },
              { label: 'Manage Banners', href: '/admin/media' },
              { label: 'View All Orders', href: '/admin/orders' },
              { label: 'Add New Coupon', href: '/admin/coupons' },
            ].map(a => (
              <Link
                key={a.href}
                to={a.href}
                className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#ECE0DA] hover:border-[#7A1125] hover:bg-[#FDF8F3] transition-all group text-sm"
                data-testid={`quick-action-${a.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <span className="font-medium text-[#1F1410]">{a.label}</span>
                <ArrowRight size={14} className="text-[#7A6A63] group-hover:text-[#7A1125] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
